import * as THREE from 'three';
import { getWeaponById, rarityColor } from './weaponRuntime.js';

const PICKUP_RADIUS = 0.55;
const PICKUP_INTERACT_DISTANCE = 1.5;
const PICKUP_DESPAWN_MS = 8000;

function computeDps(weapon) {
  if (!weapon?.runtime) return 0;
  const damage = Math.max(0, Number(weapon.runtime.baseDamage) || 0);
  const cooldown = Math.max(0.02, Number(weapon.runtime.cooldown) || 0.02);
  return damage / cooldown;
}

function createPickupMesh(weapon) {
  const group = new THREE.Group();
  const colorHex = Number.parseInt(String(rarityColor(weapon?.rarity || 'common')).replace('#', ''), 16) || 0xffffff;
  const cube = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 0.5, 0.5),
    new THREE.MeshStandardMaterial({
      color: colorHex,
      emissive: colorHex,
      emissiveIntensity: 0.5,
      roughness: 0.35,
      metalness: 0.2,
    }),
  );
  cube.castShadow = true;
  group.add(cube);

  const light = new THREE.PointLight(colorHex || 0xffffff, 0.8, 2.8, 1.8);
  light.position.set(0, -0.2, 0);
  group.add(light);
  group.userData.pickupLight = light;
  return group;
}

export const pickupRuntime = {
  _game: null,
  _pickups: [],
  _idCounter: 0,

  init(game) {
    this._game = game;
    this._pickups.length = 0;
    this._idCounter = 0;
  },

  update(delta) {
    const game = this._game;
    const player = game?.player;
    if (!game || !player?.alive || game.state !== 'playing') {
      game?.hud?.hidePickupPrompt?.();
      return;
    }

    const now = Date.now();
    let nearest = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    for (let i = this._pickups.length - 1; i >= 0; i--) {
      const pickup = this._pickups[i];
      const elapsedMs = now - pickup.spawnTime;
      if (elapsedMs >= pickup.despawnDuration) {
        this._removePickupAt(i);
        continue;
      }

      pickup.t += delta;
      pickup.mesh.rotation.y += delta * 1.5;
      pickup.mesh.position.y = pickup.baseY + Math.sin(pickup.t * 2.8) * 0.12;
      const pulse = 0.85 + Math.sin(pickup.t * 3.3) * 0.15;
      const light = pickup.mesh.userData.pickupLight;
      if (light) light.intensity = 0.75 * pulse;

      const dx = player.x - pickup.position.x;
      const dz = player.z - pickup.position.z;
      const distance = Math.hypot(dx, dz);
      pickup.isNearPlayer = distance <= PICKUP_INTERACT_DISTANCE;

      if (pickup.isNearPlayer && distance < nearestDistance) {
        nearest = pickup;
        nearestDistance = distance;
      }
    }

    if (!nearest) {
      game.hud?.hidePickupPrompt?.();
      return;
    }

    const equipPressed = game.input.consumePickupEquipPressed?.() || game.input.consumeKey('KeyE');
    const discardPressed = game.input.consumePickupDiscardPressed?.() || game.input.consumeKey('KeyF');

    if (equipPressed) {
      const hand = player.getPreferredHudHand?.() || 'right';
      player.swapHandWeapon(hand, nearest.weaponData.id);
      game.audio?.play?.('itemPickup', { rate: 1.04 + Math.random() * 0.05 });
      game.toast?.(`${String(nearest.weaponData.name || nearest.weaponData.id).toUpperCase()} EQUIPPED`);
      this._removePickupById(nearest.id);
      game.hud?.hidePickupPrompt?.();
      return;
    }

    if (discardPressed) {
      this._removePickupById(nearest.id);
      game.hud?.hidePickupPrompt?.();
      return;
    }

    const currentDps = Number(player.getCurrentWeaponDps?.() || 0);
    const newDps = Number(nearest.weaponDps || computeDps(nearest.weaponData));
    const timerPercent = Math.max(0, 1 - (now - nearest.spawnTime) / nearest.despawnDuration);
    game.hud?.renderPickupPrompt?.(nearest.weaponData.name || 'Unknown Weapon', currentDps, newDps, timerPercent);
  },

  spawnPickup(weaponData, position) {
    const game = this._game;
    if (!game || !weaponData || !position) return null;
    const mesh = createPickupMesh(weaponData);
    const x = Number(position.x) || 0;
    const z = Number(position.z) || 0;
    const y = Number(position.y) || 0;
    const baseY = y + 0.45;
    mesh.position.set(x, baseY, z);
    game.scene.add(mesh);

    const pickup = {
      id: `pickup-${Date.now()}-${this._idCounter++}`,
      weaponData,
      weaponDps: computeDps(weaponData),
      position: new THREE.Vector3(x, y, z),
      mesh,
      spawnTime: Date.now(),
      despawnDuration: PICKUP_DESPAWN_MS,
      isNearPlayer: false,
      baseY,
      radius: PICKUP_RADIUS,
      t: Math.random() * Math.PI * 2,
    };
    this._pickups.push(pickup);
    return pickup;
  },

  clear() {
    for (let i = this._pickups.length - 1; i >= 0; i--) this._removePickupAt(i);
    this._pickups.length = 0;
    this._game?.hud?.hidePickupPrompt?.();
  },

  _removePickupById(id) {
    const idx = this._pickups.findIndex((pickup) => pickup.id === id);
    if (idx >= 0) this._removePickupAt(idx);
  },

  _removePickupAt(idx) {
    const pickup = this._pickups[idx];
    if (!pickup) return;
    pickup.mesh?.parent?.remove?.(pickup.mesh);
    this._pickups.splice(idx, 1);
  },
};

export function resolveWeaponForGroundPickup(weaponId, rarity = null) {
  return getWeaponById(weaponId, { rarityOverride: rarity || null });
}
